<template>
  <transition v-if="product && !(isOutOfStock || isComingSoon)" name="fade">
    <div
      v-if="showStickyCta"
      class="w-full sticky z-1 lg:bg-white lg:shadow-md lg:fixed <lg:bottom-0"
      :style="{ top: stickyHeaderTop }"
    >
      <base-sticky name="stickyProductCta">
        <div class="px-4 flex gap-4 items-center <lg:py-4" style="min-height: 4rem">
          <base-picture
            :alt="galleryItems[0].alt"
            class="shrink-0 h-16 w-a <lg:hidden"
            height="64"
            :src="galleryItems[0].src"
            width="59"
          />
          <div class="f-col gap-2 truncate <lg:hidden">
            <span
              class="subtitle-7 title-3 truncate"
              data-dynatrace="product-name"
              data-test-id="product-name"
            >
              {{ product.name }}
            </span>
            <product-pricing
              v-if="productPrice"
              class="text-sm"
              :currency="product.currency"
              :price="productPrice"
            />
          </div>
          <div class="ml-a shrink-0 <lg:flex <lg:gap-1 <lg:w-full <lg:between">
            <vf-button
              aria-live="polite"
              class="b-grey-10 flex-1"
              :disabled="pending || addToCartLoading"
              :loading="addToCartLoading"
              :size="{ sm: 'sm', md: 'xs' }"
              :variant="isElevated ? 'primary-inverse' : 'primary'"
              @click="addToCart"
            >
              {{ isSignInToBuy ? $t.signInToBuy : $t.addToCart }}
            </vf-button>
          </div>
        </div>
      </base-sticky>
    </div>
  </transition>
</template>

<script lang="ts" setup>
const {
  addToCart,
  addToCartLoading,
  galleryItems,
  isComingSoon,
  isOutOfStock,
  isSignInToBuy,
  pending,
  product,
  productPrice
} = usePdp()
const { $t, $viewport } = useNuxtApp()
const route = useRoute()
const stickyState = useSticky()
const header = useHeaderStore()
const { y } = useScroll(document)
const { showStickyHeader } = useAppConfig().pages.pdp
const { top: productDetailsCtaTop } = useElementBounding(() => stickyState.value.productDetailsCta)

const isElevated = computed(() => product.value?.elevatedPDPDetails?.enable)

const updateHeader = () => {
  if (product.value) setPageHeader(route, { transparent: !!product.value?.elevatedPDPDetails?.enable })
}

onMounted(async () => {
  // Prevent the page from flickering when the product is available in the payload (f.e. after hydration)
  updateHeader()
  await until(pending).toBe(false)
  // When user navigates client-side and we need to wait for the product to be loaded
  updateHeader()

  watch([isOutOfStock, isComingSoon], (conditions) =>
    header.expanded = conditions.some((val) => val), { immediate: true })
})

const isVisible = useElementVisibility(useState('productDetailsCtaRef'))

const showStickyCta = computed(
  () => $viewport.breakpoint === 'sm' ? true : y.value > header.height.navigation && !isVisible.value
)

const stickyHeaderTop = computed(
  () => !showStickyHeader && !header.pageScrolledUp
    ? '0'
    : productDetailsCtaTop.value < 0 && !header.pageScrolledUp
      ? pxToRem(header.sticky.offset)
      : pxToRem(header.sticky.margin)
)
</script>
